import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Award, Calendar, Hash, LogOut } from 'lucide-react';
import { useUser } from '../context/UserContext';

const Account = () => {
  const navigate = useNavigate();
  const { customer, loyaltyAccount, isLoggedIn, logout } = useUser();

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
    }
  }, [isLoggedIn, navigate]);

  if (!customer) {
    return null;
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const fullName = [customer.given_name, customer.family_name]
    .filter(Boolean)
    .join(' ') || 'Customer';

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Account</h1>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>

          {/* Profile Card */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                <User className="w-10 h-10 text-primary-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{fullName}</h2>
                <p className="text-gray-500">{customer.phone_number}</p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-2xl shadow-sm p-8 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Contact Information</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium text-gray-900">
                    {customer.email_address || 'Not provided'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium text-gray-900">
                    {customer.phone_number || 'Not provided'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Loyalty Information */}
          {loyaltyAccount && (
            <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl shadow-sm p-8 mb-6 text-white">
              <div className="flex items-center gap-2 mb-4">
                <Award className="w-6 h-6" />
                <h3 className="text-lg font-bold">Loyalty Rewards</h3>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-white/80">Current Balance</p>
                  <p className="text-3xl font-bold">{loyaltyAccount.balance ?? 0}</p>
                  <p className="text-xs text-white/70">points</p>
                </div>
                <div>
                  <p className="text-sm text-white/80">Lifetime Points</p>
                  <p className="text-3xl font-bold">{loyaltyAccount.lifetime_points ?? 0}</p>
                  <p className="text-xs text-white/70">earned</p>
                </div>
              </div>
              {loyaltyAccount.enrolled_at && (
                <p className="text-sm text-white/80 mt-4">
                  Member since {formatDate(loyaltyAccount.enrolled_at)}
                </p>
              )}
            </div>
          )}

          {/* Account Details */}
          <div className="bg-white rounded-2xl shadow-sm p-8">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Account Details</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Member Since</p>
                  <p className="font-medium text-gray-900">
                    {formatDate(customer.created_at)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Account;
